import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightLeft } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { DataTable } from '../components/common/DataTable';
import { HashDisplay } from '../components/common/HashDisplay';
import { Pagination } from '../components/common/Pagination';
import { LiveIndicator } from '../components/common/LiveIndicator';
import { getTransactions, getNetworkStats } from '../lib/api';
import { formatNumber, timeAgo, getTxTypeColor } from '../lib/utils';
import { Badge } from '../components/ui/badge';

export default function TransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTxns, setTotalTxns] = useState(0);
  const navigate = useNavigate();
  const limit = 20;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [txRes, statsRes] = await Promise.all([
          getTransactions(currentPage, limit),
          getNetworkStats(),
        ]);
        setTransactions(txRes.data);
        setTotalTxns(statsRes.data.total_transactions);
      } catch (error) {
        console.error('Error fetching transactions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage]);

  const totalPages = Math.min(Math.ceil(totalTxns / limit), 100);

  const columns = [
    {
      header: 'Hash',
      cell: (row) => <HashDisplay hash={row.hash} link={`/tx/${row.hash}`} />,
    },
    {
      header: 'Block',
      cell: (row) => (
        <span 
          className="text-primary hover:underline cursor-pointer font-mono"
          onClick={(e) => { e.stopPropagation(); navigate(`/block/${row.block_height}`); }}
        >
          #{formatNumber(row.block_height, 0)}
        </span>
      ),
    },
    {
      header: 'Type',
      cell: (row) => (
        <Badge variant="outline" className={getTxTypeColor(row.type)}>
          {row.type}
        </Badge>
      ),
    },
    {
      header: 'From',
      cell: (row) => <HashDisplay hash={row.from_address} type="address" link={`/address/${row.from_address}`} />,
    },
    {
      header: 'To',
      cell: (row) => <HashDisplay hash={row.to_address} type="address" link={`/address/${row.to_address}`} />,
    },
    {
      header: 'Value',
      cell: (row) => (
        <span className="text-primary font-mono">
          {formatNumber(row.value, 4)} GG
        </span>
      ),
    },
    {
      header: 'Fee',
      cell: (row) => (
        <span className="text-muted-foreground font-mono text-sm">
          {row.fee.toFixed(6)}
        </span>
      ),
    },
    {
      header: 'Time',
      cell: (row) => <span className="text-muted-foreground text-sm">{timeAgo(row.timestamp)}</span>,
    },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="transactions-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-[#00F0FF]/20 to-transparent">
              <ArrowRightLeft className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Transactions</h1>
              <p className="text-sm text-muted-foreground">
                {formatNumber(totalTxns, 0)} transactions found
              </p>
            </div>
          </div>
          <LiveIndicator />
        </div>

        {/* Table */}
        <DataTable
          columns={columns}
          data={transactions}
          loading={loading}
          emptyMessage="No transactions found"
          onRowClick={(row) => navigate(`/tx/${row.hash}`)}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </Layout>
  );
}
